import React, {useEffect, useMemo, useCallback, useRef} from 'react'
import { connect } from 'react-redux'
import classnames from 'classnames/bind'
import PropTypes from 'prop-types';

import scrollTo from 'js/helpers/scrollTo'
import { pushBookBtnSearch, pushDataLayerSimple, pushHotelCardInteract } from 'js/helpers/gtm'

import Button from 'js/components/shared/Button';
import PriceBlock from 'js/components/PriceBlock';
import RatingView from 'js/components/shared/RatingView'
import WithTooltip from 'js/components/shared/WithTooltip';
import InfoBanner from 'js/components/shared/InfoBanner';
import StarsIco from 'js/components/shared/icons/StarsIco';
import Badge from 'js/components/shared/Badge';
import FocusableCard from 'js/components/shared/FocusableCard';
import {pushOpenDatesFilter, pushOpenRoomsFilter} from 'js/scenes/MainSearchBox/customEvents';

import HotelListCardThumb from './HotelListCardThumb'

import styles from './HotelListCard.module.scss'

const priceHelperText = window?.drupalSettings?.searchResults?.priceHelperText ?? 'Member rate'
const priceOldHelperText = window?.drupalSettings?.searchResults?.priceOldHelperText ?? 'Public rate'
const viewHotelLinkText = window?.drupalSettings?.viewAparthotelText

const CARD_AVAILABLE = 'available';
const CARD_UNACCOMODATED = 'unaccomodated';
const CARD_UNAVAILABLE = 'unavailable';
const CARD_LOADING = 'loading';

const cx = classnames.bind(styles)

const HotelListCard = function (props) {
  const {
    id,
    stars = 0,
    title,
    featureImages,
    price,
    oldPrice,
    subPrice,
    priceColor,
    reserveTitle,
    demandMention,
    breakfastCapsule,
    capsule: capsules,
    discount = {},
    chainDiscountNotification,
    chainDiscountLabel,
    negotiated,
    subTitle,
    reserveBtn,
    unaccomodated,
    totalMediaImages,
    firstImage,
    images,
    active,
    isSameHotel,
    reserveBtnUrl,
    hotelLink,
    available,
    reserveBtnUnavailable,
    unavailableText,
    discountTooltipText,
    datesApply,
    sticker,
    resultsLoading,
    eventTracking,
    rating,
    member,
    public: publicPrice,
    toggleHoverReview,
    distanceText,
    isConnected,
    className,
    topCard,
  } = props;

  const cardRef = useRef(null);

  const onHotelCardFocus = useCallback(() => {
    toggleHoverReview(id);
  }, [toggleHoverReview, id]);

  useEffect(() => {
    if (active || isSameHotel) {
      onHotelCardActive()
    }
  }, [active, isSameHotel]);

  const onHotelCardActive = () => {
    scrollTo(cardRef.current, {
      align: 'top',
      offset: -180,
      scrollAlways: false,
    })
  }

  /**
   * Handler for reserve button
   */
  const reserveBtnHandler = (e) => {
    e.stopPropagation()
    
    pushBookBtnSearch();
    pushHotelCardInteract('see rates');

    switch (cardStatus) {
      case CARD_UNAVAILABLE:
        e.preventDefault();
        pushOpenDatesFilter();
        return;
      case CARD_UNACCOMODATED:
        e.preventDefault();
        pushOpenRoomsFilter();
        return;
    }
    
    document.location = reserveBtnUrl;
  }

  const eventsTrackingLabel = eventTracking?.label

  const cardStatus = useMemo(function () {
    if (unaccomodated)
      return CARD_UNACCOMODATED;

    if (resultsLoading)
      return CARD_LOADING

    if (available && datesApply)
      return CARD_AVAILABLE;

    return CARD_UNAVAILABLE;
  }, [unaccomodated, resultsLoading, available])

  const submitColor = useMemo(function () {
    if (cardStatus === CARD_UNACCOMODATED) {
      return 'main';
    }

    if (cardStatus === CARD_AVAILABLE || !datesApply) {
      return 'black';
    }

    return 'main';
  }, [cardStatus])

  const titleId = `hotel-card-title-${id}`;

  const onLinkClick = (e, label) => {
    // Prevent hotel card click, allow link action only.
    e.stopPropagation()
    pushDataLayerSimple(eventsTrackingLabel)
    pushHotelCardInteract(label)
  }

  return (
    <FocusableCard
      tag="article"
      className={cx('root', className, { active })}
      ref={cardRef}
      aria-labelledby={titleId}
      data-cs-hotelid={id}
      onFocus={onHotelCardFocus}
    >
      {!!chainDiscountNotification?.length && (<p className={cx('info')}>
        {chainDiscountNotification}
        {!!chainDiscountLabel?.length && (<b>{chainDiscountLabel}</b>)}
      </p>)}
      <div className={cx('wrap')}>
        <HotelListCardThumb
          imagesCount={totalMediaImages?.imagedCount}
          firstImage={firstImage}
          images={images}
          fetchPriority={!!topCard}
          hotelId={id}
          sticker={sticker}
          className={cx('thumbnail')}
        />

        <div className={cx('content')}>
          <h2 id={titleId} className={cx('title')}>
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={hotelLink}
              onClick={(e) => onLinkClick(e, 'hotel link')}
            >
              {title}
              <StarsIco
                stars={stars}
                className={cx('title-stars')}
                data-stars-count={stars}
              />
            </a>
          </h2>
          <div className={cx('info-wrap')}>
            <div className={cx('left')}>
              {!!rating?.value && (
                <RatingView
                  vote={rating.total}
                  value={rating.value}
                  showTotal
                />
              )}

              {subTitle && (<p className={cx('sub-title')}>{subTitle}</p>)}

              {distanceText && (<p className={cx('distance-info')}>{distanceText}</p>)}

              <div className={cx('features')}>
                {featureImages.map((item, index) => (
                  <WithTooltip tooltip={item.label} position="right" key={'feature' + index}>
                    <i className={item.className} key={item.className + index} />
                  </WithTooltip>
                ))}
              </div>
            </div>
            <div className={cx('right')}>
              {demandMention && (
                <span className={cx('demand')}>
                <i className={cx('icon', 'icon-bellRinging')} />
                  {demandMention}
              </span>
              )}
              <div className={cx('badges')}>
                {negotiated && (
                  <Badge color="pink">
                    {negotiated}
                  </Badge>
                )}
              </div>
              {cardStatus === CARD_AVAILABLE && (
                <PriceBlock
                  align="right"
                  info={subPrice}
                  breakfast={breakfastCapsule}
                  capsules={capsules}
                  priceColor={priceColor}
                  discount={discount}
                  demand={demandMention}
                  discountTooltipText={discountTooltipText}
                  priceHelperText={priceHelperText}
                  priceOldHelperText={priceOldHelperText}
                  shortVersion={isConnected}
                  defaultPrice={{price, oldPrice}}
                  memberPrice={member}
                  publicPrice={publicPrice}
                />
              )}
              {cardStatus === CARD_UNAVAILABLE && unavailableText && (
                <InfoBanner className={cx('info-banner')} color="blue" size="sm">
                  {unavailableText}
                </InfoBanner>
              )}
              {cardStatus === CARD_UNACCOMODATED && unavailableText && (
                <InfoBanner className={cx('info-banner')} color="blue" size="sm">
                  {unavailableText}
                </InfoBanner>
              )}
              {reserveTitle && (
                <div className={cx('reserve-title')}>
                  {reserveTitle}
                </div>
              )}
            </div>
          </div>
          <div className={cx('nav-wrap')}>
            {viewHotelLinkText && (<a 
              href={hotelLink} 
              onClick={(e) => onLinkClick(e, 'see aparthotel')} 
              rel="noopener noreferrer" 
              target="_blank"
              aria-hidden="true"
              tabIndex={-1}
              className={cx('main-link')}
            >
              {viewHotelLinkText} &gt;
            </a>)}
            {cardStatus === CARD_LOADING && (
              <div className={cx('card-loading', 'loading-animation')} />
            )}
            {cardStatus !== CARD_LOADING && reserveBtnUrl && (
              <Button
                theme="squared"
                role="link"
                color={submitColor}
                className={cx('main-btn')}
                disabled={cardStatus === CARD_LOADING}
                onClick={reserveBtnHandler}
              >
                {cardStatus === CARD_AVAILABLE && reserveBtn}
                {cardStatus === CARD_UNAVAILABLE && reserveBtnUnavailable}
                {cardStatus === CARD_UNACCOMODATED && reserveBtnUnavailable}
              </Button>
            )}
          </div>
        </div>
      </div>
    </FocusableCard>
  )
}

const priceShape = PropTypes.shape({
  price: PropTypes.string,
  oldPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  priceColor: PropTypes.string,
})

HotelListCard.propTypes = {
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  stars: PropTypes.number,
  title: PropTypes.string,
  featureImages: PropTypes.arrayOf(PropTypes.shape({
    className: PropTypes.string,
    src: PropTypes.string,
    title: PropTypes.string,
    label: PropTypes.string,
  })).isRequired,
  price: PropTypes.string,
  oldPrice: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool,
  ]),
  subPrice: PropTypes.string,
  priceColor: PropTypes.string,
  breakfastCapsule: PropTypes.string,
  capsule: PropTypes.arrayOf(PropTypes.object),
  discount: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  chainDiscountNotification: PropTypes.string,
  chainDiscountLabel: PropTypes.string,
  negotiated: PropTypes.string,
  reserveTitle: PropTypes.string,
  demandMention: PropTypes.string,
  subTitle: PropTypes.string,
  reserveBtn: PropTypes.string,
  reserveBtnForMoblie: PropTypes.string,
  unaccomodated: PropTypes.bool,
  totalMediaImages: PropTypes.shape({
    imagedCount: PropTypes.number,
  }),
  firstImage: PropTypes.object,
  images: PropTypes.arrayOf(PropTypes.shape({
    src: PropTypes.string,
    title: PropTypes.string,
  })).isRequired,
  active: PropTypes.bool,
  isSameHotel: PropTypes.bool,
  reserveBtnUrl: PropTypes.string,
  hotelLink: PropTypes.string,
  available: PropTypes.bool.isRequired,
  reserveBtnUnavailable: PropTypes.string,
  unavailableText: PropTypes.string,
  discountTooltipText: PropTypes.string,
  datesApply: PropTypes.bool.isRequired,
  sticker: PropTypes.oneOfType([
    PropTypes.array, // Empty array BE provides if we have no data for stickers
    PropTypes.shape({
      title: PropTypes.string,
      colorText: PropTypes.string,
    })
  ]),
  resultsLoading: PropTypes.bool.isRequired,
  eventTracking: PropTypes.shape({
    label: PropTypes.string,
    favorite: PropTypes.arrayOf(PropTypes.string),
  }),
  rating: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    total: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  member: priceShape,
  public: priceShape,
  toggleHoverReview: PropTypes.func.isRequired,
  distanceText: PropTypes.string,
  isConnected: PropTypes.bool,
  className: PropTypes.string,
  topCard: PropTypes.bool,
}

export default HotelListCard
